import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollManager — handles scroll behavior on route + hash changes.
 *
 * - When the user navigates to a new path with no hash, scroll to top.
 * - When the URL has a hash (#section), scroll the matching element into view.
 *   We retry briefly because lazy-loaded routes may render the target after
 *   the initial commit.
 *
 * This must be rendered inside <BrowserRouter>.
 */
const ScrollManager: React.FC = () => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const target = hash.replace('#', '');
            // Retry up to ~1s for lazy-loaded sections to mount.
            let attempts = 0;
            const tryScroll = () => {
                const el = document.getElementById(target);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    return;
                }
                if (attempts++ < 10) {
                    window.setTimeout(tryScroll, 100);
                }
            };
            tryScroll();
        } else {
            // No hash → reset scroll on route change.
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    }, [pathname, hash]);

    return null;
};

export default ScrollManager;
